import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ListAlt as CatalogIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const GestionCatalogoCampos = () => {
  const { user } = useAuth();
  const [campos, setCampos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre_campo: '',
    etiqueta_display: '',
    tipo_campo: 'texto',
    descripcion: ''
  });

  const tiposCampo = [
    { value: 'texto', label: 'Texto', color: 'default' },
    { value: 'texto_largo', label: 'Texto Largo', color: 'default' },
    { value: 'numero', label: 'Número', color: 'primary' },
    { value: 'decimal', label: 'Decimal', color: 'primary' },
    { value: 'fecha', label: 'Fecha', color: 'secondary' },
    { value: 'fecha_hora', label: 'Fecha y Hora', color: 'secondary' },
    { value: 'booleano', label: 'Sí/No', color: 'success' },
    { value: 'archivo', label: 'Archivo', color: 'warning' },
    { value: 'imagen', label: 'Imagen', color: 'warning' },
    { value: 'seleccion', label: 'Selección Simple', color: 'info' },
    { value: 'seleccion_multiple', label: 'Selección Múltiple', color: 'info' },
    { value: 'ubicacion', label: 'Ubicación GPS', color: 'error' },
    { value: 'email', label: 'Email', color: 'default' },
    { value: 'telefono', label: 'Teléfono', color: 'default' },
    { value: 'url', label: 'URL', color: 'default' }
  ];

  useEffect(() => {
    cargarCampos();
  }, []);

  const cargarCampos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/v1/catalogo-campos`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      setCampos(response.data);
    } catch (err) {
      console.error('Error cargando campos:', err);
      setError('Error al cargar el catálogo de campos');
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirDialog = (campo = null) => {
    if (campo) {
      setEditando(campo);
      setFormData({
        nombre_campo: campo.nombre_campo,
        etiqueta_display: campo.etiqueta_display,
        tipo_campo: campo.tipo_campo,
        descripcion: campo.descripcion || ''
      });
    } else {
      setEditando(null);
      setFormData({
        nombre_campo: '',
        etiqueta_display: '',
        tipo_campo: 'texto',
        descripcion: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCerrarDialog = () => {
    setDialogOpen(false);
    setEditando(null);
    setFormData({
      nombre_campo: '',
      etiqueta_display: '',
      tipo_campo: 'texto',
      descripcion: ''
    });
  };

  const handleGuardar = async () => {
    try {
      if (!formData.nombre_campo || !formData.etiqueta_display) {
        setError('El nombre técnico y la etiqueta son obligatorios');
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${user.token}` }
      };

      if (editando) {
        // Actualizar
        await axios.put(
          `${API_URL}/api/v1/catalogo-campos/${editando.id_campo}`,
          formData,
          config
        );
      } else {
        // Crear
        await axios.post(
          `${API_URL}/api/v1/catalogo-campos`,
          formData,
          config
        );
      }

      handleCerrarDialog();
      cargarCampos();
      setError(null);
    } catch (err) {
      console.error('Error guardando campo:', err);
      setError(err.response?.data?.error || 'Error al guardar el campo');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de desactivar este campo?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/v1/catalogo-campos/${id}`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      cargarCampos();
    } catch (err) {
      console.error('Error eliminando campo:', err);
      setError('Error al desactivar el campo');
    }
  };

  const getTipoLabel = (tipo) => {
    return tiposCampo.find(t => t.value === tipo)?.label || tipo;
  };

  const getTipoColor = (tipo) => {
    return tiposCampo.find(t => t.value === tipo)?.color || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink color="inherit" href="/admin">
          Admin
        </MuiLink>
        <Typography color="text.primary">Catálogo de Campos</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CatalogIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Catálogo de Campos Dinámicos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administra los campos que podrás asignar a los estados de las empresas
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAbrirDialog()}
          size="large"
        >
          Nuevo Campo
        </Button>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nombre Técnico</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Etiqueta Display</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No hay campos en el catálogo. Crea tu primer campo.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    campos.map((campo) => (
                      <TableRow key={campo.id_campo} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                            {campo.nombre_campo}
                          </Typography>
                        </TableCell>
                        <TableCell>{campo.etiqueta_display}</TableCell>
                        <TableCell>
                          <Chip
                            label={getTipoLabel(campo.tipo_campo)}
                            color={getTipoColor(campo.tipo_campo)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {campo.descripcion || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleAbrirDialog(campo)}
                            title="Editar"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleEliminar(campo.id_campo)}
                            title="Desactivar"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={handleCerrarDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editando ? 'Editar Campo' : 'Crear Nuevo Campo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nombre Técnico"
              value={formData.nombre_campo}
              onChange={(e) => setFormData({ ...formData, nombre_campo: e.target.value })}
              placeholder="fecha_programada"
              helperText="Sin espacios, solo letras minúsculas y guiones bajos"
              disabled={!!editando}
              fullWidth
              required
            />
            <TextField
              label="Etiqueta Display"
              value={formData.etiqueta_display}
              onChange={(e) => setFormData({ ...formData, etiqueta_display: e.target.value })}
              placeholder="Fecha Programada"
              helperText="Lo que verán los usuarios"
              fullWidth
              required
            />
            <TextField
              select
              label="Tipo de Campo"
              value={formData.tipo_campo}
              onChange={(e) => setFormData({ ...formData, tipo_campo: e.target.value })}
              disabled={!!editando}
              fullWidth
              required
            >
              {tiposCampo.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={tipo.label} color={tipo.color} size="small" />
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Descripción"
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción del campo (opcional)"
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCerrarDialog}>Cancelar</Button>
          <Button onClick={handleGuardar} variant="contained">
            {editando ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GestionCatalogoCampos;
